import * as React from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import Link from "next/link";
import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import { useAuth } from "../util/auth";

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        borderRadius: 20,
        padding: theme.spacing(5),
        width: "min(90vw, 620px)",
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0 30px 80px rgba(15, 23, 42, 0.28)",
        backdropFilter: "blur(16px)",
    },
    layout: {
        display: "flex",
        gap: theme.spacing(4),
        alignItems: "flex-start",
        [theme.breakpoints.down("xs")]: {
            flexDirection: "column",
            alignItems: "flex-start",
        },
    },
    emblem: {
        width: 84,
        height: 84,
        borderRadius: "18px",
        background: "linear-gradient(135deg, rgba(236,122,150,0.16), rgba(236,122,150,0.04))",
        color: theme.palette.primary.main,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    featureList: {
        marginTop: theme.spacing(3),
        display: "grid",
        gap: theme.spacing(1.5),
        padding: 0,
        marginBottom: 0,
        listStyle: "none",
    },
    featureItem: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        color: theme.palette.text.secondary,
    },
    featureIcon: {
        color: theme.palette.success.main,
        fontSize: "1.5rem",
    },
    actions: {
        marginTop: theme.spacing(4),
        justifyContent: "space-between",
        [theme.breakpoints.down("xs")]: {
            flexDirection: "column",
            alignItems: "stretch",
            gap: theme.spacing(2),
        },
    },
    ctaButton: {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        textTransform: "none",
        fontWeight: 600,
    },
    secondaryAction: {
        textTransform: "none",
    },
    badge: {
        alignSelf: "flex-start",
        marginBottom: theme.spacing(2.5),
        fontWeight: 500,
        backgroundColor: "rgba(15,23,42,0.04)",
    },
    divider: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        opacity: 0.35,
    },
}));

const upgradeFeatures = [
    "100 more daily sections for uninterrupted storytelling",
    "Access to our most creative AI model",
    "Early access to experimental features",
];

export default function PayMe(props) {
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

    const auth = useAuth();
    const alreadyHasPlan = Boolean(auth.user && auth.user.planName);

    const open = Boolean(props.open);

    const title = alreadyHasPlan ? "Enjoying your plan?" : "Continue your story";
    const description = alreadyHasPlan
        ? "Daily credits refresh at midnight UTC. Upgrade to Bard when you're ready for longer sessions and premium features."
        : "You’ve reached the end of today’s free sections. Upgrade to keep the narrative flowing and help us keep MyTales beautifully ad-free.";

    const badgeLabel = props.runOut ? "Out of credits" : alreadyHasPlan ? "Member perk" : "Upgrade required";
    const ctaLabel = alreadyHasPlan ? "Explore Bard plan" : "See plans";

    return (
        <Dialog
            open={open}
            fullScreen={fullScreen}
            onClose={props.dismissCallback}
            aria-labelledby="payme-modal-title"
            aria-describedby="payme-modal-description"
            BackdropProps={{ style: { backdropFilter: "blur(4px)", backgroundColor: "rgba(12, 13, 19, 0.65)" } }}
            classes={{ paper: classes.dialogPaper }}
        >
            <DialogContent>
                <Chip label={badgeLabel} size="small" className={classes.badge} />
                <Box className={classes.layout}>
                    <Box className={classes.emblem}>
                        <MenuBookIcon style={{ fontSize: "2rem" }} />
                    </Box>
                    <Box>
                        <Typography id="payme-modal-title" variant="h4" gutterBottom style={{ fontWeight: 600, letterSpacing: "-0.01em" }}>
                            {title}
                        </Typography>
                        <Typography id="payme-modal-description" variant="body1" color="textSecondary">
                            {description}
                        </Typography>
                        {!alreadyHasPlan && (
                            <Box className={classes.featureList} component="ul">
                                {upgradeFeatures.map((feature) => (
                                    <Box key={feature} component="li" className={classes.featureItem}>
                                        <CheckCircleOutlineIcon className={classes.featureIcon} />
                                        <Typography variant="body2">{feature}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {alreadyHasPlan && (
                            <>
                                <Divider className={classes.divider} />
                                <Typography variant="body2" color="textSecondary">
                                    Ready for more? Bard unlocks triple the daily quota, faster responses, and early access to experimental tools.
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button onClick={props.dismissCallback} color="default" className={classes.secondaryAction}>
                    Keep exploring
                </Button>
                <Link href="/pricing" passHref>
                    <Button
                        component="a"
                        variant="contained"
                        color="primary"
                        size="large"
                        className={classes.ctaButton}
                    >
                        {ctaLabel}
                    </Button>
                </Link>
            </DialogActions>
        </Dialog>
    );
}
